import type PolicyAcceptanceModel from '@linc-technologies/linced-common/models/people/policy-acceptance';
import type Person from '@linc-technologies/linced-common/models/person';
import type Policy from '@linc-technologies/linced-common/models/policy';
import moment from 'moment';

// The set of policies a person may be required to accept now lives in the
// people service (the `policies` collection) rather than being hardcoded here.
// Callers load the policies for the school's jurisdiction (e.g.
// `store.query('policy', { jurisdiction })`) and pass them to these helpers.
// Acceptances are matched back to a policy by its `id`.

// One acceptance of a policy: the fragment, or the plain object that
// buildPolicyAcceptances makes.
export type PolicyAcceptance = Pick<
  PolicyAcceptanceModel,
  'policyId' | 'acceptedDate'
>;

// PolicyPerson is the person who accepts the policies.
type PolicyPerson = Pick<Person, 'policyAcceptances'>;

// A saved policy: these helpers match acceptances to its id.
type PolicyRecord = Pick<Policy, 'createTime'> & { id: string };

// hasAcceptedPolicy reports whether the person has accepted the given policy.
// Each version of a policy is its own record (matched by id), so an acceptance
// is matched back to a policy purely by its policyId.
export function hasAcceptedPolicy(
  person: PolicyPerson,
  policy: PolicyRecord,
): boolean {
  // TS-NARROWED: the code accepts a null person. The policy-acceptance service
  // never sends one.
  const acceptances = (person && person.policyAcceptances) || [];
  // Use filter().length rather than some(): policyAcceptances is an Ember
  // fragmentArray, which does not implement the native Array.some.
  return (
    acceptances.filter((acceptance) => acceptance.policyId === policy.id)
      .length > 0
  );
}

// latestPolicy returns the single most recently created policy (by
// createTime), or null if there are none. Only the latest policy's
// acceptance matters: a person who accepted it is not asked to also accept
// older policy versions they may have missed.
export function latestPolicy<P extends PolicyRecord>(
  policies: readonly P[] | null,
): P | null {
  return (policies || []).reduce<P | null>((latest, policy) => {
    if (!latest || (policy.createTime || 0) > (latest.createTime || 0)) {
      return policy;
    }
    return latest;
  }, null);
}

// buildPolicyAcceptances returns a new acceptances array recording the given
// policy as accepted on the given date. An existing acceptance for the same
// policy is updated in place; acceptances for any other policies are preserved
// so historical records are never dropped.
export function buildPolicyAcceptances(
  person: PolicyPerson,
  policy: PolicyRecord,
  date: string = moment().format('YYYY-MM-DD'),
): PolicyAcceptance[] {
  // TS-NARROWED: the code accepts a null person. The policy-acceptance service
  // never sends one.
  const acceptances = ((person && person.policyAcceptances) || []).map(
    (acceptance) => ({
      policyId: acceptance.policyId,
      acceptedDate: acceptance.acceptedDate,
    }),
  );

  const existing = acceptances.find(
    (acceptance) => acceptance.policyId === policy.id,
  );
  if (existing) {
    existing.acceptedDate = date;
  } else {
    acceptances.push({
      policyId: policy.id,
      acceptedDate: date,
    });
  }

  return acceptances;
}
